// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { MfaEnrollmentBanner } from '@common/components/MfaEnrollmentBanner';
import { Alert, Button } from '@imprivata-cloud/design-system';
import { createHrefWithCurrentSearch } from '@utils/navigationSearch';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SessionStorageKeys } from '@/common/utils/sessionStorage';
import { RootRoutes } from '@/index';
import { SelfServicePath } from '@/self-service/SelfServiceRoutes';
import { getLandingPage } from '../../../api/portalServices';
import type { GetLandingPageResponse } from '../../../api/types';
import DefaultLogo from '../../../assets/default_logo.svg';
import { useSession } from '../../../common/authorization/context/Session';
import {
  getDaysUntilExpiration,
  PASSWORD_EXPIRATION_WARNING_DAYS,
} from '../../../common/utils/passwordExpiration';
import { useErrorContext } from '../../../errorHandler/context/ErrorContext';
import { AppError } from '../../../errorHandler/errors';
import { isAdminRole, productTags } from '../../constants';
import { usePasswordExpirationInfo } from '../../hooks/usePasswordExpirationInfo';
import { ProductCard } from './cards/ProductCard';
import { UnpurchasedProductCard } from './cards/UnpurchasedProductCard';
import './PortalLanding.less';

const TRANSLATION_ROOT = 'portal.landing.password-expiration-banner';

const byDisplayName = (locale: string) => {
  const { compare } = new Intl.Collator(locale, { sensitivity: 'accent' });
  return <T extends { displayName: string }>(a: T, b: T) => compare(a.displayName, b.displayName);
};

export const PortalLanding = () => {
  const { emitError } = useErrorContext();
  const [data, setData] = useState<GetLandingPageResponse>();
  const { tenantId, workflowId, userInfo } = useSession();
  const [isLoading, setIsLoading] = useState<boolean>();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const { data: passwordInfo } = usePasswordExpirationInfo();
  const [alertDismissed, setAlertDismissed] = useState(
    () =>
      sessionStorage.getItem(SessionStorageKeys.PASSWORD_EXPIRATION_DISMISSED_PORTAL) === 'true',
  );
  const [searchParams] = useSearchParams();
  const daysUntilExpiration = getDaysUntilExpiration(passwordInfo?.passwordExpiresAt);
  const showPasswordAlert =
    daysUntilExpiration !== null &&
    daysUntilExpiration > 0 &&
    daysUntilExpiration <= PASSWORD_EXPIRATION_WARNING_DAYS &&
    !alertDismissed;

  useEffect(() => {
    const getPortalInfo = async () => {
      return await getLandingPage(workflowId || '', tenantId || '');
    };
    setIsLoading(true);
    getPortalInfo()
      .then(response => {
        setData(response);
      })
      .catch(e => {
        emitError(new AppError(e));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [workflowId, tenantId, emitError]);

  // TODO: Remove this when local testing fixes the return of "string" for logos
  const resolvedLogoUrl =
    (data?.orgInfo?.logoUrl && data?.orgInfo?.logoUrl !== 'string' && data.orgInfo.logoUrl) ||
    undefined;

  const isAdmin = isAdminRole(userInfo?.roleType);
  const { purchased, learnMore } = useMemo(() => {
    const compare = byDisplayName(locale);
    return {
      purchased: (data?.productInfo?.purchasedProducts ?? [])
        .filter(product => product.productTag !== productTags.SSO || isAdmin)
        .sort(compare),
      learnMore: (data?.productInfo?.nonPurchasedProducts ?? [])
        .filter(product => product.productTag !== productTags.SSO)
        .sort(compare),
    };
  }, [data, isAdmin, locale]);

  const passwordAlertActions = (
    <Button
      variant="default"
      size="sm"
      className="password-alert-link"
      onClick={() => {
        const url = createHrefWithCurrentSearch(
          `${RootRoutes.SELF_SERVICE}${SelfServicePath.SELF_SERVICE_IDENTITY_PASSWORD}`,
          searchParams,
        );
        // deepcode ignore OR: URL is built from constant route paths and current search params. The startsWith('/') guard ensures only a relative URL reaches window.open.
        /* c8 ignore next */
        window.open(url.startsWith('/') ? url : RootRoutes.SELF_SERVICE, '_blank', 'noopener,noreferrer');
      }}
    >
      {t(`${TRANSLATION_ROOT}.update-button`)}
    </Button>
  );

  return (
    <div className="landing">
      {showPasswordAlert && (
        <div className="password-expiration-alert">
          <Alert
            status="warning"
            title={t(`${TRANSLATION_ROOT}.title`, { count: daysUntilExpiration })}
            message={t(`${TRANSLATION_ROOT}.message`)}
            actions={passwordAlertActions}
            onClose={() => {
              setAlertDismissed(true);
              sessionStorage.setItem(
                SessionStorageKeys.PASSWORD_EXPIRATION_DISMISSED_PORTAL,
                'true',
              );
            }}
          />
        </div>
      )}
      <MfaEnrollmentBanner />
      <section className="landing-title-bar">
        <h1 className="h1">{t('portal.landing.title')}</h1>
        {!isLoading && (
          <img className="landing-logo" src={resolvedLogoUrl || DefaultLogo} alt="" />
        )}
      </section>
      {purchased.length > 0 && (
        <section className="landing-panel">
          <div className="purchased-grid">
            {purchased.map(product => (
              <ProductCard key={product.productTag} product={product} tenantId={tenantId || ''} />
            ))}
          </div>
        </section>
      )}
      {learnMore.length > 0 && (
        <section className="landing-panel">
          <h2 className="h2">{t('portal.landing.explore-heading')}</h2>
          <div className="learn-more-grid">
            {learnMore.map(product => (
              <UnpurchasedProductCard key={product.productTag} product={product} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
